import geometry from '../../../../../scene/util/geometry';
import productService from '../../../../../services/products';
import validationErrors from '../validationErrors';
import { ITEMS } from '../../../../../constants';

const SECTION_HEIGHT_73 = 730;
const SECTION_HEIGHT_124 = 1240;
const SECTION_HEIGHT_179 = 1785;
const SECTION_HEIGHT_226 = 2265;
const MAX_NO_OF_BOTTLE_RACKS_OR_FELT_SHELVES_PER_SECTION = 6;

/**
 * Gets the weight or stability limits for a specific section
 *
 * @param {Object} section The section to look up
 * @param {String} limitType The type of limits requested ('weight' or 'stability')
 * @returns {Number} The max/min points limit for the section
 */
const getSectionLimit = (section, limitType) => {
  if (section.height === SECTION_HEIGHT_73)
    return limitType === 'weight' ? 4 : 2;
  if (section.height === SECTION_HEIGHT_124)
    return limitType === 'weight' ? 7 : 3;
  if (section.height === SECTION_HEIGHT_179)
    return limitType === 'weight' ? 8 : 4;
  if (section.height === SECTION_HEIGHT_226)
    return limitType === 'weight' ? 9 : 5;
  return 0;
};

/**
 * Gets no of points a specific insert should add to the section total
 *
 * @param {Object} item The insert to look up
 * @param {String} type The type of points requested ('weight' or 'stability')
 * @param {Object} [parent] The section containing the insert
 * @returns {Number} The no of points the insert should add
 */
const getInsertPoints = (item, type, parent) => {
  switch (item.filter.type) {
    case ITEMS.DRAWER:
    case ITEMS.SHELF:
      return 1;
    case ITEMS.BOTTLE_RACK:
    case ITEMS.FELT_SHELF:
      return type === 'weight' ? 1 : 0.5;
    case ITEMS.CHEST:
    case ITEMS.TABLE:
      return type === 'weight' ? 3 : 2;
    case ITEMS.CABINET:
      return type === 'weight'
        ? item.height > 1000
          ? 6
          : 3
        : item.height > 1000
        ? parent.height > 2000
          ? 3
          : 4
        : 2;
    default:
      return 0;
  }
};

/**
 * Checks whether a section has exceeded the max limit for number of bottle racks / felt shelves
 *
 * @param {Object} inserts All inserts in the section
 * @returns {Boolean} True if there are too many bottle racks or felt shelves in total
 */
const tooManyBottleRacksOrFeltShelves = inserts => {
  const noOfBottleRacksAndFeltShelves = inserts.filter(insert =>
    productService.isType(insert, [ITEMS.BOTTLE_RACK, ITEMS.FELT_SHELF])
  ).length;

  return (
    noOfBottleRacksAndFeltShelves >
    MAX_NO_OF_BOTTLE_RACKS_OR_FELT_SHELVES_PER_SECTION
  );
};

/**
 * Gets the (eventual) weight errors for a section
 *
 * @param {Object} section The section to validate
 * @returns {Array} A list of all weight errors found on the section
 */
const getWeightErrors = section => {
  const weightErrors = [];

  const inserts = section.items.filter(productService.isInsert);

  const totalWeightPoints = inserts.reduce((total, item) => {
    return total + (getInsertPoints(item, 'weight', section) || 0);
  }, 0);

  const maxWeightPointsInSection = getSectionLimit(section, 'weight');

  if (
    totalWeightPoints > maxWeightPointsInSection ||
    tooManyBottleRacksOrFeltShelves(inserts)
  ) {
    weightErrors.push({
      ...validationErrors.WEIGHT_LIMIT_EXCEEDED,
      itemid: section.itemid,
    });
  }

  return weightErrors;
};

/**
 * Gets the dimensions and coordinates of the three "zones of interest"
 * (top, bottom, middle) needed for the stability validation
 *
 * @param {Object} section The section to get zones for
 * @returns {Array} A list of the three zones in the requested section
 */
const getStabilityZones = section => {
  const { width, height, depth } = section;
  const localSection = { x: 0, y: 0, z: 0, width, height, depth };

  if (section.height === SECTION_HEIGHT_73) {
    const bottom = {
      ...localSection,
      y: 0,
      height: 0.475 * section.height,
    };

    const top = {
      ...localSection,
      y: section.height - 0.475 * section.height,
      height: 0.475 * section.height,
    };

    return [bottom, top];
  }

  // 4% gap between zones to prevent shelves and bottle racks
  // to reach into more than one
  const bottom = {
    ...localSection,
    y: 0,
    height: 0.31 * section.height,
  };

  const middle = {
    ...localSection,
    y: section.height / 2 - 0.15 * section.height,
    height: 0.4 * section.height,
  };

  const top = {
    ...localSection,
    y: section.height - 0.21 * section.height,
    height: 0.21 * section.height,
  };

  return [bottom, middle, top];
};

/**
 * Gets the (eventual) stability errors for a section
 *
 * @param {Object} section The section to validate
 * @param {Object} inserts A list fo all the inserts in the section
 * @param {Object} tac The complete TAC
 * @returns {Array} A list of all stability errors found on the section
 */
const getStabilityErrors = (section, inserts, tac) => {
  const stabilityErrors = [];

  const insertsWithPoints = inserts.map(insert => ({
    ...insert,
    stabilityPoints: getInsertPoints(insert, 'stability', section),
  }));

  const totalStabilityPoints = insertsWithPoints.reduce((total, insert) => {
    return total + (insert.stabilityPoints || 0);
  }, 0);

  const sectionLimit = getSectionLimit(section, 'stability');
  if (totalStabilityPoints < sectionLimit) {
    stabilityErrors.push({
      ...validationErrors.INSERTS_TOO_FEW,
      itemid: section.itemid,
    });
  }

  if (
    !getStabilityZones(section).every(area =>
      insertsWithPoints.some(
        insert => insert.stabilityPoints && geometry.collides(insert, area)
      )
    )
  ) {
    stabilityErrors.push({
      ...(section.height === SECTION_HEIGHT_73
        ? validationErrors.STABILITY_ZONES_UNCOVERED_IVAR_LOW_SECTION
        : validationErrors.STABILITY_ZONES_UNCOVERED_IVAR),
      itemid: section.itemid,
    });
  }

  // SPECIAL CASE:
  // The 179cm high section with only a high cabinet inside is stable
  // only if merged with at least one other section
  if (
    section.height === SECTION_HEIGHT_179 &&
    inserts.length === 1 &&
    totalStabilityPoints === 4 &&
    tac.items.filter(
      item => productService.isSection(item) && geometry.collides(section, item)
    ).length < 2
  ) {
    stabilityErrors.push({
      ...validationErrors.HIGH_CABINET_UNSTABLE,
      itemid: section.itemid,
    });
  }

  return stabilityErrors;
};

/**
 * Validates an IVAR TAC according to the weight and stability rules
 *
 * @param {Object} tac The complete TAC to validate
 * @returns {Array} A list of all weight and stability errors found in the TAC
 */
const validate = tac => {
  const allErrors = tac.items.reduce((errors, item) => {
    if (productService.isSection(item)) {
      const inserts = item.items.filter(productService.isInsert);
      errors.push(
        ...getWeightErrors(item, inserts),
        ...getStabilityErrors(item, inserts, tac)
      );
    }
    return errors;
  }, []);

  return allErrors;
};

export default {
  validate,
};
